import type { CSSProperties } from 'react';

/**
 * WEB-1697 — РАМКА БЛОКА: толщина, цвет и скругление одной точкой правды.
 *
 * Репорт владельца 24.08, повторённый дословно дважды: «для всех элементов, у
 * кого есть рамка, три настройки: толщина рамки, цвет рамки и скругление
 * рамки». Поимённо названы «Кнопка-ссылка», «Значок наличия», «Промо-билет»,
 * «Блок цены», кнопки портала.
 *
 * КАК БЫЛО. Настройки не было ни одной, а рамки зашиты числами прямо в ветках
 * отрисовки — порядка сорока мест, у каждого своё значение: `1px dashed` у
 * заглушек, `1px solid` у карточек, `2px solid` у поля ввода и кнопки-ссылки,
 * `4px solid` у аватара. Протащить три новых поля через сорок веток руками
 * значило бы завести сорок мест, которые разъедутся при первой же правке.
 *
 * ПОЭТОМУ ОБРАЗЕЦ — `blockSpacing.tsx` (WEB-1680), а не сорок правок:
 *  1. СПРАВОЧНИК УМОЛЧАНИЙ «тип блока → его сегодняшняя рамка». Пусто в панели
 *     = ровно то число, что стояло в ветке до правки, у КАЖДОГО типа своё;
 *  2. одна функция собирает итоговый стиль (умолчание, перекрытое авторскими
 *     значениями), и ветка блока зовёт ЕЁ вместо зашитых чисел;
 *  3. перечень типов, которым панель предлагает настройку, = ключи справочника.
 *     Показать её типу без рамки нельзя — получилась бы мёртвая настройка
 *     (класс WEB-1677).
 *
 * ⛔ ЖИВЫЕ ВОРОНКИ НЕ ТРОГАЕМ. Никаких миграций: воронка без заданных полей
 * рисуется прежним стилем этого типа — это и обеспечивает справочник.
 *
 * ═══ ЧТО ПОД НАСТРОЙКУ НЕ ПОПАДАЕТ — РЕШЕНО И ЗАПИСАНО ═══
 *
 *  • ПУНКТИРНЫЕ ЗАГЛУШКИ (`1px dashed` на местах, где картинка ещё не
 *    загружена) — это СЛУЖЕБНЫЙ вид редактора, а не оформление воронки.
 *    Настройке не подчиняются: автор, поменявший толщину рамки блока, не
 *    должен получить другой вид «пустого места».
 *  • РАМКА ВЫБРАННОГО/НЕВЫБРАННОГО ВАРИАНТА (`--option-selected-border` /
 *    `--option-border-color`) — это СОСТОЯНИЕ, а не оформление блока, и у него
 *    уже есть свои поля (WEB-1674). Второе поле на то же свойство заводить
 *    нельзя (шрам WEB-1677 про два поля с необъявленным старшинством).
 *  • ЛИНИЯ ОТРЫВА ПРОМО-БИЛЕТА — часть рисунка билета (имитация перфорации), а
 *    не рамка блока: автор, поменявший толщину рамки, не должен испортить
 *    перфорацию.
 *  • ОПАСНОЕ СОСТОЯНИЕ кнопки отмены (красноватая рамка подтверждения) —
 *    предупреждение, а не оформление: авторский цвет действует на обычное
 *    состояние, опасное остаётся красным.
 *
 * ═══ ГРАНИЦА ЭТОГО ЗАХОДА, НАЗВАННАЯ ВСЛУХ ═══
 *
 * Механизм заведён и подключён к типам, чья рамка — чистое оформление и живёт
 * в общих рендерерах: «Кнопка-ссылка», «Блок цены», «Карточка», а с WEB-1825 и
 * «Отзывы».
 *
 * ⛔ ПОЧЕМУ ЧЕТВЁРТЫЙ ТИП ЖДАЛ ПОЛГОДА — ЭТО БЫЛА НЕ ЛЕНЬ, А УПЁРТЫЙ ГЕЙТ.
 * Каждое цветовое поле блока обязано быть в реестре записи
 * (block-schema/color-fields.ts), а оттуда оно САМО попадает в каталог для
 * модели, у которого стоял жёсткий потолок длины в 26000 символов — решение PM
 * 2026-08-01, «поднимать нельзя, упрётся снова — резать каталог структурно».
 * Строка «Отзывов» выводила каталог за потолок на два десятка символов.
 *
 * Владелец 31.08 пересмотрел приоритет: «ИИ-чат должен уметь менять любые
 * настройки, которые есть у блоков… лимиты — ерунда». Потолок поднят на
 * фактическую стоимость с замером (block-catalog-prompt.spec.ts), «Отзывы»
 * заведены строкой в справочнике ниже.
 *
 * Остальные рамки (значок наличия — только у одного вида из пяти; промо-билет
 * и кнопки портала — в собственных компонентах) ждут НЕ потолка, а разбора
 * своих веток: механизм для них готов — достаточно строки в справочнике.
 *
 * ⚠️ У КНОПКИ-ССЫЛКИ ЗАЛИВКА СНИМАЕТ УМОЛЧАТЕЛЬНУЮ ОБВОДКУ, НО НЕ АВТОРСКУЮ —
 * уточнено 31.08 (WEB-1823), прежняя формулировка отстала от кода.
 *  • Заданный «Фон кнопки» по-прежнему гасит обводку, которая приходит сама
 *    (буквой темы или умолчанием справочника): залитая кнопка без единого
 *    поля рамки выглядит ровно как до WEB-1823, байт-в-байт;
 *  • но ЯВНО заданные автором толщина или цвет рамки возвращают её ПОВЕРХ
 *    заливки — надстройка `buttonBorderOverride` кладётся последней в
 *    `buttonSurfaceStyle`. Это канон владельца 31.08 «явный выбор автора
 *    сильнее темы и автоконтраста» (AC WEB-1823), а не дефект слоёв;
 *  • скругление рамкой НЕ считается: автор, тронувший одно скругление,
 *    обводку не заказывал — на залитой кнопке она не воскресает.
 * Закреплено на живой разметке в `__tests__/buttonBorder.web1823.test.tsx`
 * (пары «заливка + толщина/цвет» и «заливка + только скругление»).
 *
 * Сторож
 * `__tests__/blockBorder.web1697.test.tsx` считает зашитые рамки в обоих
 * рендерерах и краснеет, если их станет БОЛЬШЕ, — то есть новая зашитая рамка
 * мимо справочника не проедет, а старые снимаются по мере заведения типов.
 */

export type BlockBorderStyle = 'solid' | 'dashed';

export interface BlockBorderDefault {
  /** Толщина в px — ровно та, что стояла в ветке до правки. */
  width: number;
  style: BlockBorderStyle;
  /** CSS-значение цвета, обычно переменная темы с фолбэком. */
  color: string;
  /** CSS-значение скругления, обычно переменная темы с фолбэком. */
  radius: string;
}

/**
 * СПРАВОЧНИК УМОЛЧАНИЙ: тип блока → его сегодняшняя рамка. Значения списаны с
 * веток отрисовки один в один — на это стоит поимённая проверка в стороже.
 */
export const BLOCK_BORDER_DEFAULTS: Readonly<Record<string, BlockBorderDefault>> = {
  // PublicBlockRenderer, ветка 'button_link'.
  button_link: {
    width: 2,
    style: 'solid',
    color: 'var(--color-primary, #1677ff)',
    radius: 'var(--border-radius-button, 8px)',
  },
  // PublicBlockRenderer, ветка 'price_block' — денежный блок.
  price_block: {
    width: 2,
    style: 'solid',
    color: 'var(--color-primary, #1677ff)',
    radius: 'var(--border-radius-lg, 12px)',
  },
  // PublicBlockRendererExtended, ветка 'card' — вид «сплошная».
  card: {
    width: 1,
    style: 'solid',
    color: 'var(--color-border-primary, #f0f0f0)',
    radius: 'var(--border-radius-lg, 12px)',
  },
  // WEB-1825: PublicBlockRendererExtended, ветка 'testimonials' — рамка
  // КАРТОЧКИ ОТЗЫВА. Значения списаны с ветки один в один, как у всех
  // остальных: живая воронка без заданных полей выглядит байт-в-байт как до
  // правки. Тип ждал не механизма (он готов с WEB-1697), а решения по потолку
  // каталога для модели — оно принято владельцем 31.08.
  testimonials: {
    width: 1,
    style: 'solid',
    color: 'var(--color-border-primary, #f0f0f0)',
    radius: 'var(--border-radius-lg, 12px)',
  },
  /**
   * WEB-1829: PublicBlockRendererExtended, ветка 'feature_chips' — рамка
   * ПЛАШКИ. Значения списаны с ветки один в один, как у всех остальных.
   *
   * Почему волосяная СВЕТЛАЯ линия, а не переменная темы: заливка плашки по
   * умолчанию — полупрозрачная ТЁМНАЯ (rgba(0,0,0,0.35), см. ветку рендера), и
   * такой она остаётся на обеих темах. Тёмная плашка на светлом фоне — не
   * случайность, а канон соседней подписи-пилюли поверх фото (WEB-1684:
   * «тёмная плашка, белый текст»): сплошная заливка по переменной темы на
   * светлой теме дала бы белую плашку на белом фоне, то есть невидимую сетку.
   * Раз плашка тёмная всегда — граница обязана быть светлой всегда.
   *
   * Скругление 14 — с примера-скриншота владельца 31.08.
   */
  feature_chips: {
    width: 1,
    style: 'solid',
    color: 'rgba(255, 255, 255, 0.14)',
    radius: '14px',
  },
};

/**
 * Вид блока меняет умолчание: у «стеклянной» карточки рамка своя —
 * полупрозрачная белая, и пусто у неё обязано означать ЕЁ, а не рамку
 * сплошного вида. Тот же случай, что «живая иногда» настройка в WEB-1705:
 * состояние блока — часть признака, а не помеха ему.
 */
const GLASS_CARD_BORDER: BlockBorderDefault = {
  width: 1,
  style: 'solid',
  color: 'rgba(255,255,255,0.20)',
  radius: 'var(--border-radius-lg, 12px)',
};

/**
 * WEB-1823 — КНОПОЧНЫЕ ТИПЫ: рамка есть, а СТАТИЧНОГО умолчания нет.
 *
 * Канон владельца 31.08 (коммент в WEB-1651): «кнопка — такой же блок, как
 * остальные», значит и три настройки рамки у неё обязаны быть. Но в
 * справочник выше кнопки положить НЕЛЬЗЯ, и это не обход правила, а его
 * соблюдение: справочник обещает «пусто = вот ЭТО число», а сегодняшняя рамка
 * кнопки приходит из ТЕМЫ (`primaryButtonStyle`: вид solid рамки не рисует,
 * вид outlined рисует 2px акцентом). Записать сюда одно число значило бы
 * соврать про половину тем и поменять вид живых воронок.
 *
 * Поэтому у них свой перечень, а «пусто» означает «как решит тема»: надстройка
 * ниже на пустых полях не отдаёт НИЧЕГО, и разметка остаётся байт-в-байт
 * прежней. Перечень зеркалит бэковский `BLOCK_BORDER_BLOCK_TYPES`
 * (block-schema/block-border.ts) — сверку обоих множеств держит сторож
 * `block-border.web1697.spec.ts`, читающий этот файл с диска.
 *
 * Список — ровно те места, что зовут `buttonSurfaceStyle(block)` целым блоком.
 * Кнопка внутри urgency_banner сюда НЕ входит намеренно: у того блока «элемент»
 * — сам баннер, кнопка на нём деталь, и поля рамки блока красили бы не её
 * (та же граница, что проведена в WEB-1711 для `innerBackground`).
 */
export const BUTTON_SURFACE_BORDER_TYPES: ReadonlySet<string> = new Set([
  'button_next',
  'button_link',
  'button_to_pay',
  'stripe_checkout_form',
  'return_to_app_button',
  'close_webview_button',
  // CTA внутри «героя» — единственная кнопка блока, поля адресуют её.
  'hero',
]);

/**
 * Чем добираются НЕЗАДАННЫЕ поля, когда автор тронул рамку кнопки частично.
 * Не свои числа, а ССЫЛКА на справочник button_link: у кнопки-ссылки рамку
 * рисуют ДВА слоя (resolveBlockBorder в ветке + надстройка ниже поверх
 * заливки), и разойтись они могут только если фолбэки набраны дважды руками.
 */
const BUTTON_BORDER_FALLBACK: BlockBorderDefault = BLOCK_BORDER_DEFAULTS.button_link;

/**
 * Надстройка рамки для кнопочных мест: то, что автор задал ЯВНО, и ничего
 * сверх. Зовётся из `buttonSurfaceStyle` (blockBackgroundStyle.tsx) ПОСЛЕ
 * заливки — та ставит `border: 'none'`, чтобы залитая кнопка не носила
 * обводку темы, и явный выбор автора обязан быть сильнее (AC WEB-1823:
 * «явные настройки автора сильнее темы/автоконтраста»).
 *
 * ⚠ ТОЛЩИНА/ЦВЕТ и СКРУГЛЕНИЕ разделены НАМЕРЕННО. Скругление — не заказ
 * обводки: у button_link ратифицировано (WEB-1697), что заданный фон обводку
 * снимает, и автор, тронувший одно скругление, не должен получить её обратно
 * на уже живой воронке. Поэтому `border` появляется, только когда задана
 * толщина или цвет; скругление приезжает само по себе.
 */
export function buttonBorderOverride(fields: {
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
}): CSSProperties {
  const style: CSSProperties = {};
  const hasWidth = typeof fields.borderWidth === 'number';
  const hasColor = typeof fields.borderColor === 'string' && fields.borderColor !== '';
  if (hasWidth || hasColor) {
    const width = fields.borderWidth ?? BUTTON_BORDER_FALLBACK.width;
    const color = fields.borderColor || BUTTON_BORDER_FALLBACK.color;
    style.border =
      width === 0 ? 'none' : `${width}px ${BUTTON_BORDER_FALLBACK.style} ${color}`;
  }
  if (typeof fields.borderRadius === 'number') {
    style.borderRadius = `${fields.borderRadius}px`;
  }
  return style;
}

/**
 * Кому панель предлагает три настройки рамки: типы со справочником умолчаний
 * ПЛЮС кнопочные места (WEB-1823). Одно множество на панель — иначе гейт
 * разъедется с тем, что реально применяет рендер, и вернётся класс мёртвой
 * настройки WEB-1677 (его ловит сторож «поле не действует там, где настройка
 * не показана» в blockBorder.web1697.test.tsx).
 */
export const BLOCK_BORDER_PANEL_TYPES: ReadonlySet<string> = new Set([
  ...Object.keys(BLOCK_BORDER_DEFAULTS),
  ...BUTTON_SURFACE_BORDER_TYPES,
]);

export interface BlockBorderFields {
  type: string;
  /** WEB-1697 — толщина рамки, px. Absent = умолчание своего типа. */
  borderWidth?: number;
  /** WEB-1697 — цвет рамки. Absent = умолчание своего типа. */
  borderColor?: string;
  /** WEB-1697 — скругление углов, px. Absent = умолчание своего типа. */
  borderRadius?: number;
  /** Вид карточки: у «стеклянной» своё умолчание рамки. */
  cardVariant?: 'solid' | 'glass';
}

/** Сегодняшняя рамка типа с учётом его вида — она же «пусто» в панели. */
export function defaultBorderOf(fields: BlockBorderFields): BlockBorderDefault | undefined {
  if (fields.type === 'card' && fields.cardVariant === 'glass') {
    return GLASS_CARD_BORDER;
  }
  return BLOCK_BORDER_DEFAULTS[fields.type];
}

/** Тронул ли автор рамку. Нет → ветка рисует ровно прежний стиль. */
export function hasBlockBorder(fields: {
  borderWidth?: number;
  borderColor?: string;
  borderRadius?: number;
}): boolean {
  return (
    typeof fields.borderWidth === 'number' ||
    typeof fields.borderRadius === 'number' ||
    (typeof fields.borderColor === 'string' && fields.borderColor !== '')
  );
}

/**
 * Итоговый стиль рамки блока: умолчание своего типа, перекрытое тем, что задал
 * автор. Ветки отрисовки зовут ЭТУ функцию вместо зашитых чисел — одна точка
 * правды на оба рендерера.
 *
 * Тип без рамки в справочнике получает пустой стиль: подмешивать `border:
 * undefined` в ветку, у которой рамки нет, нечего.
 */
export function resolveBlockBorder(fields: BlockBorderFields): CSSProperties {
  const base = defaultBorderOf(fields);
  if (!base) return {};
  const width = fields.borderWidth ?? base.width;
  const color = fields.borderColor || base.color;
  const radius =
    typeof fields.borderRadius === 'number' ? `${fields.borderRadius}px` : base.radius;
  return {
    border: width === 0 ? 'none' : `${width}px ${base.style} ${color}`,
    borderRadius: radius,
  };
}
